import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, Select } from '@/components/ui/field';
import { EmptyState, LoadingState } from '@/components/ui/states';
import { useCurrency } from '@/Hooks/useCurrency';
import {
  type CatalogItem,
  type Meal,
  type MealPrice,
  apiError,
  apiMoneyStep,
} from './lib';

type Selections = Record<string, Record<string, number>>;

export default function MealsTab({ items }: { items: CatalogItem[] }) {
  const { t } = useTranslation();
  const { format, decimals } = useCurrency();
  // `CreateMealComponentOptionDto.extraCharge` is validated with
  // `@IsNumber({ maxDecimalPlaces: 2 })`, so the input never offers a third
  // decimal the endpoint would reject.
  const chargeStep = apiMoneyStep(decimals);
  const { can } = usePermission();
  const canUpdate = can('menu.update');

  const [meals, setMeals] = useState<CatalogItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Meal | null>(null);
  const [loading, setLoading] = useState(true);

  const [groupDraft, setGroupDraft] = useState({ name: '', minSelect: '1', maxSelect: '1' });
  const [optionDraft, setOptionDraft] = useState<Record<string, { itemId: string; extraCharge: string }>>(
    {},
  );

  const [variantId, setVariantId] = useState('');
  const [selections, setSelections] = useState<Selections>({});
  const [price, setPrice] = useState<MealPrice | null>(null);
  const [priceError, setPriceError] = useState<string | null>(null);

  const loadMeals = useCallback(() => {
    setLoading(true);
    api
      .get<{ data: CatalogItem[] }>('/meals', { params: { limit: 100 } })
      .then(({ data }) => {
        const list = data.data ?? [];
        setMeals(list);
        setSelectedId((current) => current ?? list[0]?.id ?? null);
      })
      .catch((e) => toast.error(apiError(e, t('catalog.meals.loadFailed'))))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(loadMeals, [loadMeals]);

  const loadDetail = useCallback(
    (id: string) => {
      api
        .get<Meal>(`/meals/${id}`)
        .then(({ data }) => {
          setDetail(data);
          setVariantId('');
          // Pre-select the defaults so the preview opens on the meal as sold.
          const initial: Selections = {};
          for (const group of data.componentGroups ?? []) {
            for (const option of group.options ?? []) {
              if (option.isDefault) {
                initial[group.id] = { ...(initial[group.id] ?? {}), [option.id]: 1 };
              }
            }
          }
          setSelections(initial);
        })
        .catch((e) => toast.error(apiError(e, t('catalog.meals.loadFailed'))));
    },
    [t],
  );

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  /** §6.2 — the server owns meal pricing, so the preview asks it rather than
   *  re-implementing the arithmetic in the browser. */
  useEffect(() => {
    if (!detail) return;
    const payload = {
      variantId: variantId || undefined,
      selections: Object.entries(selections).flatMap(([groupId, options]) =>
        Object.entries(options)
          .filter(([, quantity]) => quantity > 0)
          .map(([optionId, quantity]) => ({ groupId, optionId, quantity })),
      ),
    };
    const timer = setTimeout(() => {
      api
        .post<MealPrice>(`/meals/${detail.id}/price`, payload)
        .then(({ data }) => {
          setPrice(data);
          setPriceError(null);
        })
        .catch((e) => {
          setPrice(null);
          setPriceError(apiError(e, t('catalog.meals.priceFailed')));
        });
    }, 200);
    return () => clearTimeout(timer);
  }, [detail, variantId, selections, t]);

  /**
   * Why `POST /meals/:id/groups` would refuse this draft, in the service's own
   * terms — null when it would take it.
   *
   * `MealsService.assertSelectionRules` runs on `dto.isRequired ?? true`, and
   * this form never sends `isRequired`, so every group it creates is required
   * and a minimum of zero is rejected outright rather than stored.
   */
  const groupRuleError = (() => {
    const min = Number(groupDraft.minSelect);
    const max = Number(groupDraft.maxSelect);
    if (min > max) return t('catalog.modifiers.minOverMax');
    if (max < 1) return t('catalog.meals.maxAtLeastOne');
    if (min < 1) return t('catalog.modifiers.requiredNeedsMin');
    return null;
  })();

  /** Picks in one group, summed over quantities — the same count
   *  `MealsService.price` compares against `minSelect`/`maxSelect`. */
  const picksIn = (groupId: string) =>
    Object.values(selections[groupId] ?? {}).reduce((sum, quantity) => sum + quantity, 0);

  const setQuantity = (groupId: string, optionId: string, quantity: number) => {
    setSelections((current) => ({
      ...current,
      [groupId]: { ...(current[groupId] ?? {}), [optionId]: Math.max(0, quantity) },
    }));
  };

  const addGroup = async () => {
    if (!detail || !groupDraft.name.trim() || groupRuleError) return;
    try {
      await api.post(`/meals/${detail.id}/groups`, {
        name: groupDraft.name,
        minSelect: Number(groupDraft.minSelect) || 0,
        maxSelect: Number(groupDraft.maxSelect) || 1,
      });
      setGroupDraft({ name: '', minSelect: '1', maxSelect: '1' });
      loadDetail(detail.id);
    } catch (e) {
      toast.error(apiError(e, t('catalog.meals.saveFailed')));
    }
  };

  const removeGroup = async (groupId: string) => {
    if (!detail) return;
    try {
      await api.delete(`/meals/${detail.id}/groups/${groupId}`);
      loadDetail(detail.id);
    } catch (e) {
      toast.error(apiError(e, t('catalog.meals.saveFailed')));
    }
  };

  const addOption = async (groupId: string) => {
    if (!detail) return;
    const draft = optionDraft[groupId];
    if (!draft?.itemId) return;
    try {
      await api.post(`/meals/${detail.id}/groups/${groupId}/options`, {
        itemId: draft.itemId,
        extraCharge: Number(draft.extraCharge) || 0,
      });
      setOptionDraft({ ...optionDraft, [groupId]: { itemId: '', extraCharge: '0' } });
      loadDetail(detail.id);
    } catch (e) {
      toast.error(apiError(e, t('catalog.meals.saveFailed')));
    }
  };

  const toggleDefault = async (groupId: string, optionId: string, isDefault: boolean) => {
    if (!detail) return;
    try {
      await api.patch(`/meals/${detail.id}/groups/${groupId}/options/${optionId}`, { isDefault });
      loadDetail(detail.id);
    } catch (e) {
      toast.error(apiError(e, t('catalog.meals.saveFailed')));
    }
  };

  const removeOption = async (groupId: string, optionId: string) => {
    if (!detail) return;
    try {
      await api.delete(`/meals/${detail.id}/groups/${groupId}/options/${optionId}`);
      loadDetail(detail.id);
    } catch (e) {
      toast.error(apiError(e, t('catalog.meals.saveFailed')));
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[16rem_1fr_18rem]">
      <Card>
        <CardHeader>
          <CardTitle>{t('catalog.meals.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <LoadingState label={t('common.loading')} />
          ) : meals.length === 0 ? (
            <EmptyState title={t('catalog.meals.empty')} hint={t('catalog.meals.emptyHint')} />
          ) : (
            <ul className="divide-y rounded-lg border">
              {meals.map((meal) => (
                <li key={meal.id}>
                  <button
                    onClick={() => setSelectedId(meal.id)}
                    className={`w-full p-3 text-start text-sm hover:bg-hover ${
                      meal.id === selectedId ? 'bg-accent-soft font-medium text-accent-soft-fg' : ''
                    }`}
                  >
                    {meal.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <div className="space-y-4">
        {!detail ? (
          <Card>
            <CardContent className="p-5">
              <EmptyState title={t('catalog.meals.nothingSelected')} />
            </CardContent>
          </Card>
        ) : (
          <>
            {(detail.componentGroups ?? []).map((group) => (
              <Card key={group.id}>
                <CardHeader className="flex-row items-start justify-between gap-2">
                  <div>
                    <CardTitle>{group.name}</CardTitle>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      <Badge tone="info">
                        {t('catalog.modifiers.rule', { min: group.minSelect, max: group.maxSelect })}
                      </Badge>
                      {group.isRequired && (
                        <Badge tone="warning">{t('catalog.modifiers.required')}</Badge>
                      )}
                    </div>
                  </div>
                  {canUpdate && (
                    <Button size="sm" variant="danger" onClick={() => removeGroup(group.id)}>
                      {t('common.delete')}
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  {(group.options ?? []).length === 0 ? (
                    <p className="text-sm text-ink-muted">
                      {t('catalog.meals.noOptions')}
                    </p>
                  ) : (
                    <ul className="divide-y rounded-lg border">
                      {group.options?.map((option) => {
                        const chosen = selections[group.id]?.[option.id] ?? 0;
                        return (
                          <li
                            key={option.id}
                            className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm"
                          >
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-medium">{option.item?.name ?? option.itemId}</span>
                                {option.isDefault && (
                                  <Badge tone="brand">{t('catalog.meals.defaultOption')}</Badge>
                                )}
                                {Number(option.extraCharge) > 0 ? (
                                  <Badge tone="warning">
                                    {t('catalog.meals.substitutionCharge', {
                                      amount: format(option.extraCharge),
                                    })}
                                  </Badge>
                                ) : (
                                  <Badge tone="success">{t('catalog.meals.included')}</Badge>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setQuantity(group.id, option.id, chosen - 1)}
                                disabled={chosen === 0}
                                aria-label={t('catalog.meals.decrease')}
                              >
                                −
                              </Button>
                              <span className="w-6 text-center">{chosen}</span>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setQuantity(group.id, option.id, chosen + 1)}
                                // One pick past `maxSelect` and the price
                                // endpoint refuses the whole selection, so the
                                // preview would only ever answer with an error.
                                disabled={picksIn(group.id) >= group.maxSelect}
                                title={
                                  picksIn(group.id) >= group.maxSelect
                                    ? t('catalog.meals.atMaxSelect', { count: group.maxSelect })
                                    : undefined
                                }
                                aria-label={t('catalog.meals.increase')}
                              >
                                +
                              </Button>
                              {canUpdate && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() =>
                                      toggleDefault(group.id, option.id, !option.isDefault)
                                    }
                                  >
                                    {t(
                                      option.isDefault
                                        ? 'catalog.meals.clearDefault'
                                        : 'catalog.meals.makeDefault',
                                    )}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => removeOption(group.id, option.id)}
                                  >
                                    {t('common.delete')}
                                  </Button>
                                </>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {canUpdate && (
                    <div className="flex flex-wrap items-end gap-2">
                      <Select
                        className="w-56"
                        value={optionDraft[group.id]?.itemId ?? ''}
                        onChange={(e) =>
                          setOptionDraft({
                            ...optionDraft,
                            [group.id]: {
                              itemId: e.target.value,
                              extraCharge: optionDraft[group.id]?.extraCharge ?? '0',
                            },
                          })
                        }
                        aria-label={t('catalog.meals.addOption')}
                      >
                        <option value="">{t('catalog.meals.chooseComponent')}</option>
                        {items.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </Select>
                      <Input
                        className="w-28"
                        type="number"
                        step={chargeStep}
                        min="0"
                        aria-label={t('catalog.meals.extraCharge')}
                        value={optionDraft[group.id]?.extraCharge ?? '0'}
                        onChange={(e) =>
                          setOptionDraft({
                            ...optionDraft,
                            [group.id]: {
                              itemId: optionDraft[group.id]?.itemId ?? '',
                              extraCharge: e.target.value,
                            },
                          })
                        }
                      />
                      <Button
                        size="sm"
                        onClick={() => addOption(group.id)}
                        // `CreateMealComponentOptionDto.itemId` is @IsNotEmpty.
                        disabled={!optionDraft[group.id]?.itemId}
                        title={
                          optionDraft[group.id]?.itemId
                            ? undefined
                            : t('catalog.meals.chooseComponent')
                        }
                      >
                        {t('common.add')}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}

            {canUpdate && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('catalog.meals.addGroup')}</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-wrap items-end gap-3">
                  <Field label={t('catalog.items.name')}>
                    <Input
                      className="w-56"
                      value={groupDraft.name}
                      onChange={(e) => setGroupDraft({ ...groupDraft, name: e.target.value })}
                    />
                  </Field>
                  <Field label={t('catalog.modifiers.minSelect')} error={groupRuleError ?? undefined}>
                    <Input
                      className="w-24"
                      type="number"
                      // A group created here is required, so one pick is the
                      // floor the server will accept.
                      min="1"
                      value={groupDraft.minSelect}
                      onChange={(e) => setGroupDraft({ ...groupDraft, minSelect: e.target.value })}
                    />
                  </Field>
                  <Field label={t('catalog.modifiers.maxSelect')}>
                    <Input
                      className="w-24"
                      type="number"
                      min="1"
                      value={groupDraft.maxSelect}
                      onChange={(e) => setGroupDraft({ ...groupDraft, maxSelect: e.target.value })}
                    />
                  </Field>
                  <Button
                    onClick={addGroup}
                    disabled={!groupDraft.name.trim() || !!groupRuleError}
                    title={
                      groupRuleError ??
                      (groupDraft.name.trim() ? undefined : t('catalog.items.nameRequired'))
                    }
                  >
                    {t('common.add')}
                  </Button>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      <Card className="h-fit">
        <CardHeader>
          <CardTitle>{t('catalog.meals.pricePreview')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!detail ? (
            <p className="text-sm text-ink-muted">
              {t('catalog.meals.nothingSelected')}
            </p>
          ) : (
            <>
              {(detail.variants ?? []).length > 0 && (
                <Field label={t('catalog.meals.variant')}>
                  <Select value={variantId} onChange={(e) => setVariantId(e.target.value)}>
                    <option value="">{t('catalog.meals.noVariant')}</option>
                    {detail.variants?.map((variant) => (
                      <option key={variant.id} value={variant.id}>
                        {variant.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}

              {priceError ? (
                <p className="rounded-lg bg-amber-50 dark:bg-amber-950/50 p-3 text-sm text-amber-800 dark:text-amber-300">{priceError}</p>
              ) : price ? (
                <div className="space-y-2 text-sm">
                  <Row label={t('catalog.meals.base')} value={format(price.basePrice)} />
                  <Row
                    label={t('catalog.meals.variantDelta')}
                    value={format(price.variantDelta)}
                  />
                  <Row
                    label={t('catalog.meals.extras')}
                    value={format(price.extras)}
                  />
                  <div className="flex items-center justify-between border-t pt-2 text-base font-semibold">
                    <span>{t('common.total')}</span>
                    <span>{format(price.total)}</span>
                  </div>

                  {price.breakdown.length > 0 && (
                    <ul className="mt-3 space-y-1 border-t pt-2 text-xs text-ink-muted">
                      {price.breakdown.map((line) => (
                        <li key={line.optionId} className="flex justify-between gap-2">
                          <span className="truncate">
                            {line.groupName} · {line.name} × {line.quantity}
                          </span>
                          <span>{format(line.lineTotal)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <LoadingState label={t('common.loading')} />
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-ink-muted">{label}</span>
      <span>{value}</span>
    </div>
  );
}
